import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, Loader2, RotateCcw, Save, Mail, MessageSquare, ShieldAlert, Smartphone, Clock, MessageCircle } from "lucide-react";
import { AiAdvisorPane } from "@/components/AiAdvisorPane";
import { ExplainerVideosPane } from "@/components/ExplainerVideosPane";

const PLACEHOLDERS = ["{customer_name}", "{vehicle}", "{reg}", "{workshop_name}"];
const FOLLOWUP_PLACEHOLDERS = [...PLACEHOLDERS, "{item_name}", "{reconsider_url}"];
const THANKYOU_PLACEHOLDERS = [...PLACEHOLDERS, "{exit_video_url}", "{csi_survey_url}"];

export default function SettingsModal({ open, onClose }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("welcome"); // "welcome" | "followup" | "thankyou"
  const [emailTpl, setEmailTpl] = useState("");
  const [smsTpl, setSmsTpl] = useState("");
  const [fuEmailTpl, setFuEmailTpl] = useState("");
  const [fuSmsTpl, setFuSmsTpl] = useState("");
  const [delayHours, setDelayHours] = useState(48);
  const [surveyUrl, setSurveyUrl] = useState("");
  const [tyEmailTpl, setTyEmailTpl] = useState("");
  const [tySmsTpl, setTySmsTpl] = useState("");
  const [exitVideoUrl, setExitVideoUrl] = useState("");
  const [csiSurveyUrl, setCsiSurveyUrl] = useState("");
  const [defaults, setDefaults] = useState({ email: "", sms: "", fuEmail: "", fuSms: "", tyEmail: "", tySms: "" });
  // SMS branding (per-workshop alphanumeric sender ID)
  const [smsSenderId, setSmsSenderId] = useState("");
  const [smsBranding, setSmsBranding] = useState(null); // {effective_sender, default_sender, max_length}
  const [savingSender, setSavingSender] = useState(false);
  // Email branding (per-workshop "From" display name — e.g. "RRG Toyota")
  const [emailFromName, setEmailFromName] = useState("");
  const [emailBranding, setEmailBranding] = useState(null); // {effective_from_name, default_from_name}
  const [savingEmailFrom, setSavingEmailFrom] = useState(false);
  // WhatsApp channel (per-workshop toggle — WhatsApp-first with SMS fallback)
  const [whatsapp, setWhatsapp] = useState(null); // {enabled, configured, from_number, sandbox}
  const [savingWhatsapp, setSavingWhatsapp] = useState(false);
  // AI Service Advisor (per-workshop opt-in — answers customer texts about their quote)
  const [aiAdvisor, setAiAdvisor] = useState(null); // {enabled, assistant_name, handoff_message, configured}
  const [savingAi, setSavingAi] = useState(false);
  // Working hours (per-workshop) — Mon-Fri 08:30-17:00 + lunch 12:30-13:00 default
  const [hoursOpen, setHoursOpen] = useState("08:30");
  const [hoursClose, setHoursClose] = useState("17:00");
  const [hoursDays, setHoursDays] = useState([0, 1, 2, 3, 4]);
  const [lunchEnabled, setLunchEnabled] = useState(true);
  const [lunchStart, setLunchStart] = useState("12:30");
  const [lunchEnd, setLunchEnd] = useState("13:00");
  const [hoursDefaults, setHoursDefaults] = useState(null);
  const [savingHours, setSavingHours] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get("/settings").then((r) => {
      setEmailTpl(r.data.welcome_template || "");
      setSmsTpl(r.data.welcome_sms_template || "");
      setFuEmailTpl(r.data.followup_template || "");
      setFuSmsTpl(r.data.followup_sms_template || "");
      setDelayHours(r.data.followup_delay_hours || 48);
      setSurveyUrl(r.data.survey_url || "");
      setTyEmailTpl(r.data.thankyou_template || "");
      setTySmsTpl(r.data.thankyou_sms_template || "");
      setExitVideoUrl(r.data.exit_video_url || "");
      setCsiSurveyUrl(r.data.csi_survey_url || "");
      setDefaults({
        email: r.data.default_welcome_template || "",
        sms: r.data.default_welcome_sms_template || "",
        fuEmail: r.data.default_followup_template || "",
        fuSms: r.data.default_followup_sms_template || "",
        tyEmail: r.data.default_thankyou_template || "",
        tySms: r.data.default_thankyou_sms_template || "",
      });
    }).finally(() => setLoading(false));
    api.get("/workshop/sms-branding").then((r) => {
      setSmsSenderId(r.data.sender_id || "");
      setSmsBranding(r.data);
    }).catch(() => {});
    api.get("/workshop/email-branding").then((r) => {
      setEmailFromName(r.data.email_from_name || "");
      setEmailBranding(r.data);
    }).catch(() => {});
    api.get("/workshop/whatsapp").then((r) => setWhatsapp(r.data)).catch(() => {});
    api.get("/workshop/ai-advisor").then((r) => setAiAdvisor(r.data)).catch(() => {});
    api.get("/workshop/hours").then((r) => {
      const h = r.data.hours || {};
      setHoursOpen(h.open || "08:30");
      setHoursClose(h.close || "17:00");
      setHoursDays(h.days || [0, 1, 2, 3, 4]);
      if (h.lunch) {
        setLunchEnabled(true);
        setLunchStart(h.lunch.start || "12:30");
        setLunchEnd(h.lunch.end || "13:00");
      } else {
        setLunchEnabled(false);
      }
      setHoursDefaults(r.data.default_hours || null);
    }).catch(() => {});
  }, [open]);

  const saveSmsBranding = async () => {
    setSavingSender(true);
    try {
      const r = await api.patch("/workshop/sms-branding", { sender_id: smsSenderId.trim() });
      setSmsBranding(r.data);
      setSmsSenderId(r.data.sender_id || "");
      toast.success(smsSenderId.trim() ? `SMS will now appear from "${smsSenderId.trim()}"` : "Sender ID cleared — falling back to the default");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save sender ID");
    } finally { setSavingSender(false); }
  };

  const saveEmailBranding = async () => {
    setSavingEmailFrom(true);
    try {
      const r = await api.patch("/workshop/email-branding", { email_from_name: emailFromName.trim() });
      setEmailBranding(r.data);
      setEmailFromName(r.data.email_from_name || "");
      toast.success(emailFromName.trim() ? `Emails will now appear from "${emailFromName.trim()}"` : "Email sender name cleared — falling back to the default");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save email sender name");
    } finally { setSavingEmailFrom(false); }
  };

  const toggleWhatsapp = async () => {
    if (!whatsapp) return;
    setSavingWhatsapp(true);
    try {
      const r = await api.patch("/workshop/whatsapp", { enabled: !whatsapp.enabled });
      setWhatsapp(r.data);
      toast.success(r.data.enabled
        ? "WhatsApp-first is ON — messages try WhatsApp, then fall back to SMS"
        : "WhatsApp channel off — everything goes by SMS");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update WhatsApp channel");
    } finally { setSavingWhatsapp(false); }
  };

  const saveAiAdvisor = async (patch) => {
    if (!aiAdvisor) return;
    setSavingAi(true);
    try {
      const r = await api.patch("/workshop/ai-advisor", patch);
      setAiAdvisor(r.data);
      if (patch.enabled !== undefined) {
        toast.success(r.data.enabled
          ? "AI assistant is ON — it will answer customer texts about their quote"
          : "AI assistant off — every customer message waits for a person");
      } else {
        toast.success("AI assistant settings saved");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update the AI assistant");
    } finally { setSavingAi(false); }
  };

  const saveWorkingHours = async () => {
    setSavingHours(true);
    try {
      const payload = {
        open: hoursOpen,
        close: hoursClose,
        days: hoursDays,
        lunch: lunchEnabled ? { start: lunchStart, end: lunchEnd } : null,
      };
      const r = await api.patch("/workshop/hours", payload);
      const h = r.data.hours || {};
      setHoursOpen(h.open || "08:30");
      setHoursClose(h.close || "17:00");
      setHoursDays(h.days || [0, 1, 2, 3, 4]);
      setLunchEnabled(!!h.lunch);
      if (h.lunch) { setLunchStart(h.lunch.start); setLunchEnd(h.lunch.end); }
      toast.success("Working hours saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save working hours");
    } finally { setSavingHours(false); }
  };


  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/settings", {
        welcome_template: emailTpl,
        welcome_sms_template: smsTpl,
        followup_template: fuEmailTpl,
        followup_sms_template: fuSmsTpl,
        followup_delay_hours: Number(delayHours) || 48,
        survey_url: surveyUrl,
        thankyou_template: tyEmailTpl,
        thankyou_sms_template: tySmsTpl,
        exit_video_url: exitVideoUrl,
        csi_survey_url: csiSurveyUrl,
      });
      toast.success("Templates saved");
      onClose?.();
    } catch {
      toast.error("Failed to save");
    } finally { setSaving(false); }
  };

  const insertPlaceholder = (target, ph) => {
    const setters = {
      email: setEmailTpl, sms: setSmsTpl, fuEmail: setFuEmailTpl, fuSms: setFuSmsTpl,
      tyEmail: setTyEmailTpl, tySms: setTySmsTpl,
    };
    setters[target]?.((s) => s + (s && !s.endsWith(" ") ? " " : "") + ph);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-card rounded-lg border border-border shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto animate-fade-up" data-testid="settings-modal">
        <div className="flex items-start justify-between p-6 border-b border-border">
          <div>
            <div className="label-overline">Workshop settings</div>
            <h2 className="font-display text-2xl font-bold tracking-tight mt-1">Customer messages</h2>
            <p className="text-sm text-muted-foreground mt-1 max-w-md">
              Customise the wording for both the welcome message at check-in and the re-engagement follow-up.
            </p>
          </div>
          <button onClick={onClose} className="btn-outline" data-testid="settings-close-btn"><X className="w-4 h-4" /></button>
        </div>

        {/* Tabs */}
        {!loading && (
          <div className="px-6 pt-4 -mb-px flex gap-1 border-b border-border">
            <button
              type="button"
              onClick={() => setTab("welcome")}
              className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors ${tab === "welcome" ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}
              data-testid="settings-tab-welcome"
            >
              Welcome at check-in
            </button>
            <button
              type="button"
              onClick={() => setTab("followup")}
              className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors ${tab === "followup" ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}
              data-testid="settings-tab-followup"
            >
              Decline follow-up
            </button>
            <button
              type="button"
              onClick={() => setTab("thankyou")}
              className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors ${tab === "thankyou" ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}
              data-testid="settings-tab-thankyou"
            >
              Check-out (thank you)
            </button>
            <button
              type="button"
              onClick={() => setTab("smsbrand")}
              className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors ${tab === "smsbrand" ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}
              data-testid="settings-tab-smsbrand"
            >
              SMS &amp; email branding
            </button>
            <button
              type="button"
              onClick={() => setTab("hours")}
              className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors ${tab === "hours" ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}
              data-testid="settings-tab-hours"
            >
              Working hours
            </button>
            <button
              type="button"
              onClick={() => setTab("ai")}
              className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors ${tab === "ai" ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}
              data-testid="settings-tab-ai"
            >
              AI assistant
            </button>
            <button
              type="button"
              onClick={() => setTab("videos")}
              className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors ${tab === "videos" ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}
              data-testid="settings-tab-videos"
            >
              Explainer videos
            </button>
          </div>
        )}

        {loading ? (
          <div className="p-10 grid place-items-center text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin" /></div>
        ) : tab === "welcome" ? (
          <div className="p-6 space-y-6" data-testid="settings-welcome-pane">
            {/* Workshop-wide survey URL */}
            <div className="rounded-lg border border-border bg-muted/20 p-4">
              <div className="label-overline">Customer satisfaction survey</div>
              <p className="text-xs text-muted-foreground mt-1">Workshop-wide default — appears on the customer's "All done" page after handover. Per-job override available on the Handover screen.</p>
              <input
                type="url"
                value={surveyUrl}
                onChange={(e) => setSurveyUrl(e.target.value)}
                placeholder="https://surveys.toyota.co.uk/csi/..."
                className="input-default font-mono-plex text-sm mt-3"
                data-testid="settings-survey-url"
              />
            </div>
            {/* Placeholders */}
            <div>
              <div className="label-overline mb-2">Available placeholders</div>
              <div className="flex flex-wrap gap-2">
                {PLACEHOLDERS.map((ph) => (
                  <span key={ph} className="font-mono-plex text-xs px-2 py-1 rounded-md bg-muted/40 border border-border">{ph}</span>
                ))}
              </div>
            </div>

            {/* Email template */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-primary" />
                  <div className="label-overline">Email body</div>
                </div>
                <button onClick={() => setEmailTpl(defaults.email)} className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1" data-testid="settings-reset-email-btn" type="button">
                  <RotateCcw className="w-3 h-3" /> Reset to default
                </button>
              </div>
              <textarea value={emailTpl} onChange={(e) => setEmailTpl(e.target.value)} rows={9} className="input-default font-mono-plex text-sm leading-relaxed" data-testid="settings-email-template" />
              <div className="flex flex-wrap gap-1 mt-2">
                {PLACEHOLDERS.map((ph) => (
                  <button key={ph} type="button" onClick={() => insertPlaceholder("email", ph)} className="text-[10px] font-mono-plex px-2 py-1 rounded-md bg-muted/30 hover:bg-muted/60 border border-border" data-testid={`settings-email-insert-${ph}`}>+ {ph}</button>
                ))}
              </div>
            </div>

            {/* SMS template */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <MessageSquare className="w-4 h-4 text-primary" />
                  <div className="label-overline">SMS</div>
                </div>
                <button onClick={() => setSmsTpl(defaults.sms)} className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1" data-testid="settings-reset-sms-btn" type="button">
                  <RotateCcw className="w-3 h-3" /> Reset to default
                </button>
              </div>
              <textarea value={smsTpl} onChange={(e) => setSmsTpl(e.target.value)} rows={4} className="input-default font-mono-plex text-sm leading-relaxed" data-testid="settings-sms-template" />
              <div className="flex items-center justify-between mt-2">
                <div className="flex flex-wrap gap-1">
                  {PLACEHOLDERS.map((ph) => (
                    <button key={ph} type="button" onClick={() => insertPlaceholder("sms", ph)} className="text-[10px] font-mono-plex px-2 py-1 rounded-md bg-muted/30 hover:bg-muted/60 border border-border" data-testid={`settings-sms-insert-${ph}`}>+ {ph}</button>
                  ))}
                </div>
                <div className="text-[10px] font-mono-plex text-muted-foreground" data-testid="settings-sms-charcount">{smsTpl.length} chars</div>
              </div>
            </div>
          </div>
        ) : tab === "followup" ? (
          <div className="p-6 space-y-6" data-testid="settings-followup-pane">
            <div className="rounded-lg bg-muted/40 border border-border p-4 flex items-start gap-3">
              <ShieldAlert className="w-5 h-5 text-primary mt-0.5 shrink-0" />
              <div className="text-sm">
                <div className="font-semibold">Re-engagement follow-up</div>
                <p className="text-muted-foreground mt-1 leading-relaxed">
                  When a customer declines a <span className="text-destructive font-semibold">red</span> item, this message becomes available in the dashboard's Follow-ups inbox after the delay below. Includes a one-tap "Authorise" link unique to that item.
                </p>
              </div>
            </div>

            <div>
              <label className="label-overline">Delay before follow-up</label>
              <div className="flex items-center gap-3 mt-2">
                <input
                  type="number"
                  min="1"
                  max="720"
                  value={delayHours}
                  onChange={(e) => setDelayHours(e.target.value)}
                  className="input-default w-24 font-mono-plex text-sm"
                  data-testid="settings-followup-delay"
                />
                <span className="text-sm text-muted-foreground">hours after the customer declines</span>
              </div>
            </div>

            <div>
              <div className="label-overline mb-2">Available placeholders</div>
              <div className="flex flex-wrap gap-2">
                {FOLLOWUP_PLACEHOLDERS.map((ph) => (
                  <span key={ph} className="font-mono-plex text-xs px-2 py-1 rounded-md bg-muted/40 border border-border">{ph}</span>
                ))}
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-primary" />
                  <div className="label-overline">Follow-up email body</div>
                </div>
                <button onClick={() => setFuEmailTpl(defaults.fuEmail)} className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1" data-testid="settings-reset-fu-email-btn" type="button">
                  <RotateCcw className="w-3 h-3" /> Reset to default
                </button>
              </div>
              <textarea value={fuEmailTpl} onChange={(e) => setFuEmailTpl(e.target.value)} rows={9} className="input-default font-mono-plex text-sm leading-relaxed" data-testid="settings-fu-email-template" />
              <div className="flex flex-wrap gap-1 mt-2">
                {FOLLOWUP_PLACEHOLDERS.map((ph) => (
                  <button key={ph} type="button" onClick={() => insertPlaceholder("fuEmail", ph)} className="text-[10px] font-mono-plex px-2 py-1 rounded-md bg-muted/30 hover:bg-muted/60 border border-border" data-testid={`settings-fu-email-insert-${ph}`}>+ {ph}</button>
                ))}
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <MessageSquare className="w-4 h-4 text-primary" />
                  <div className="label-overline">Follow-up SMS</div>
                </div>
                <button onClick={() => setFuSmsTpl(defaults.fuSms)} className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1" data-testid="settings-reset-fu-sms-btn" type="button">
                  <RotateCcw className="w-3 h-3" /> Reset to default
                </button>
              </div>
              <textarea value={fuSmsTpl} onChange={(e) => setFuSmsTpl(e.target.value)} rows={4} className="input-default font-mono-plex text-sm leading-relaxed" data-testid="settings-fu-sms-template" />
              <div className="flex items-center justify-between mt-2">
                <div className="flex flex-wrap gap-1">
                  {FOLLOWUP_PLACEHOLDERS.map((ph) => (
                    <button key={ph} type="button" onClick={() => insertPlaceholder("fuSms", ph)} className="text-[10px] font-mono-plex px-2 py-1 rounded-md bg-muted/30 hover:bg-muted/60 border border-border" data-testid={`settings-fu-sms-insert-${ph}`}>+ {ph}</button>
                  ))}
                </div>
                <div className="text-[10px] font-mono-plex text-muted-foreground">{fuSmsTpl.length} chars</div>
              </div>
            </div>
          </div>
        ) : tab === "thankyou" ? (
          <div className="p-6 space-y-6" data-testid="settings-thankyou-pane">
            <div className="rounded-lg border border-border bg-muted/20 p-4 space-y-3">
              <div>
                <div className="label-overline">Workshop exit video URL</div>
                <p className="text-xs text-muted-foreground mt-1">A short, branded thank-you / "see you next time" clip linked from every check-out message. Vimeo, YouTube unlisted, or self-hosted.</p>
                <input
                  type="url"
                  value={exitVideoUrl}
                  onChange={(e) => setExitVideoUrl(e.target.value)}
                  placeholder="https://youtu.be/your-thank-you-video"
                  className="input-default font-mono-plex text-sm mt-2"
                  data-testid="settings-exit-video-url"
                />
              </div>
              <div>
                <div className="label-overline">Toyota CSI survey URL</div>
                <p className="text-xs text-muted-foreground mt-1">Link to the Toyota customer satisfaction survey for this dealership. Customers see this in the check-out message with a clear "9/10 means recommended" framing.</p>
                <input
                  type="url"
                  value={csiSurveyUrl}
                  onChange={(e) => setCsiSurveyUrl(e.target.value)}
                  placeholder="https://surveys.toyota.co.uk/csi/your-dealer-id"
                  className="input-default font-mono-plex text-sm mt-2"
                  data-testid="settings-csi-survey-url"
                />
              </div>
            </div>

            <div>
              <div className="label-overline mb-2">Available placeholders</div>
              <div className="flex flex-wrap gap-2">
                {THANKYOU_PLACEHOLDERS.map((ph) => (
                  <span key={ph} className="font-mono-plex text-xs px-2 py-1 rounded-md bg-muted/40 border border-border">{ph}</span>
                ))}
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-primary" />
                  <div className="label-overline">Thank-you email body</div>
                </div>
                <button onClick={() => setTyEmailTpl(defaults.tyEmail)} className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1" data-testid="settings-reset-ty-email-btn" type="button">
                  <RotateCcw className="w-3 h-3" /> Reset to default
                </button>
              </div>
              <textarea value={tyEmailTpl} onChange={(e) => setTyEmailTpl(e.target.value)} rows={11} className="input-default font-mono-plex text-sm leading-relaxed" data-testid="settings-ty-email-template" />
              <div className="flex flex-wrap gap-1 mt-2">
                {THANKYOU_PLACEHOLDERS.map((ph) => (
                  <button key={ph} type="button" onClick={() => insertPlaceholder("tyEmail", ph)} className="text-[10px] font-mono-plex px-2 py-1 rounded-md bg-muted/30 hover:bg-muted/60 border border-border" data-testid={`settings-ty-email-insert-${ph}`}>+ {ph}</button>
                ))}
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <MessageSquare className="w-4 h-4 text-primary" />
                  <div className="label-overline">Thank-you SMS</div>
                </div>
                <button onClick={() => setTySmsTpl(defaults.tySms)} className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1" data-testid="settings-reset-ty-sms-btn" type="button">
                  <RotateCcw className="w-3 h-3" /> Reset to default
                </button>
              </div>
              <textarea value={tySmsTpl} onChange={(e) => setTySmsTpl(e.target.value)} rows={4} className="input-default font-mono-plex text-sm leading-relaxed" data-testid="settings-ty-sms-template" />
              <div className="flex items-center justify-between mt-2">
                <div className="flex flex-wrap gap-1">
                  {THANKYOU_PLACEHOLDERS.map((ph) => (
                    <button key={ph} type="button" onClick={() => insertPlaceholder("tySms", ph)} className="text-[10px] font-mono-plex px-2 py-1 rounded-md bg-muted/30 hover:bg-muted/60 border border-border" data-testid={`settings-ty-sms-insert-${ph}`}>+ {ph}</button>
                  ))}
                </div>
                <div className="text-[10px] font-mono-plex text-muted-foreground">{tySmsTpl.length} chars</div>
              </div>
            </div>
          </div>
        ) : tab === "smsbrand" ? (
          <div className="p-6 space-y-6" data-testid="settings-smsbrand-pane">
            <div className="rounded-lg border border-primary/30 bg-primary/[0.04] p-4 flex items-start gap-3">
              <div className="w-9 h-9 rounded-md bg-primary/15 text-primary grid place-items-center shrink-0">
                <Smartphone className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <div className="label-overline text-primary">Branded SMS sender</div>
                <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                  Replace the raw phone number with your dealership's name on every customer text — turns *"+44 7782 218977"* into *"ToyotaHudd"* on their phone. Max <strong>11 characters</strong>, letters, numbers and spaces only.
                </p>
                <p className="text-[11px] text-muted-foreground/80 mt-2">
                  <strong className="text-foreground">Currently sending from:</strong>{" "}
                  <span className="font-mono-plex" data-testid="settings-effective-sender">
                    {smsBranding?.effective_sender || "—"}
                  </span>
                </p>
              </div>
            </div>

            <div>
              <label htmlFor="sms-sender-id" className="label-overline">Your dealership's sender ID</label>
              <input
                id="sms-sender-id"
                type="text"
                value={smsSenderId}
                onChange={(e) => setSmsSenderId(e.target.value.slice(0, 11))}
                placeholder="e.g. ToyotaHudd"
                maxLength={11}
                className="input-default font-mono-plex text-base mt-2 tracking-wider"
                data-testid="settings-sms-sender-input"
              />
              <div className="flex items-center justify-between mt-1.5 text-[11px]">
                <span className="text-muted-foreground">
                  Leave blank to use the platform default: <span className="font-mono-plex text-foreground">{smsBranding?.default_sender || "SrvSellPRO"}</span>
                </span>
                <span className="font-mono-plex text-muted-foreground">{smsSenderId.length}/11</span>
              </div>
            </div>

            <div className="rounded-lg border border-amber-500/30 bg-amber-500/[0.05] p-4 text-xs leading-relaxed">
              <div className="font-semibold text-amber-200 mb-1">A note on UK alphanumeric senders</div>
              <ul className="list-disc pl-4 space-y-1 text-muted-foreground">
                <li>One-way only — customers can't reply by SMS (they tap the share link in the body instead, which is exactly how the customer journey works).</li>
                <li>Requires a non-trial Twilio account. While the account is on trial, branded senders are auto-attempted and gracefully fall back to the real number so SMS keeps flowing.</li>
                <li>Some carriers strip non-essential characters — keep it tight and recognisable.</li>
              </ul>
            </div>

            <div className="flex justify-end">
              <button
                onClick={saveSmsBranding}
                disabled={savingSender}
                className="btn-primary"
                data-testid="settings-save-sms-sender-btn"
              >
                {savingSender ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Save sender ID
              </button>
            </div>

            {/* ---- Email sender name ------------------------------------ */}
            <div className="rounded-lg border border-primary/30 bg-primary/[0.04] p-4 flex items-start gap-3 mt-4">
              <div className="w-9 h-9 rounded-md bg-primary/15 text-primary grid place-items-center shrink-0">
                <Mail className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <div className="label-overline text-primary">Email sender name</div>
                <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                  The name customers see in their inbox on every email &mdash; pre-arrival reminders, welcome messages, quotes, follow-ups. Set this to your dealership brand (e.g. <span className="font-mono-plex text-foreground">RRG Toyota</span>) so it doesn&apos;t appear from &ldquo;ServiceSell PRO&rdquo;.
                </p>
                <p className="text-[11px] text-muted-foreground/80 mt-2">
                  <strong className="text-foreground">Currently sending from:</strong>{" "}
                  <span className="font-mono-plex" data-testid="settings-effective-email-from">
                    {emailBranding?.effective_from_name || "—"}
                  </span>
                </p>
              </div>
            </div>

            <div>
              <label htmlFor="email-from-name" className="label-overline">Your dealership&apos;s inbox name</label>
              <input
                id="email-from-name"
                type="text"
                value={emailFromName}
                onChange={(e) => setEmailFromName(e.target.value.slice(0, 64))}
                placeholder="e.g. RRG Toyota"
                maxLength={64}
                className="input-default text-base mt-2"
                data-testid="settings-email-from-input"
              />
              <div className="flex items-center justify-between mt-1.5 text-[11px]">
                <span className="text-muted-foreground">
                  Leave blank to use the platform default: <span className="font-mono-plex text-foreground">{emailBranding?.default_from_name || "ServiceSell PRO"}</span>
                </span>
                <span className="font-mono-plex text-muted-foreground">{emailFromName.length}/64</span>
              </div>
            </div>

            <div className="flex justify-end">
              <button
                onClick={saveEmailBranding}
                disabled={savingEmailFrom}
                className="btn-primary"
                data-testid="settings-save-email-from-btn"
              >
                {savingEmailFrom ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Save sender name
              </button>
            </div>

            {/* ---- WhatsApp channel ------------------------------------- */}
            <div className="rounded-lg border border-primary/30 bg-primary/[0.04] p-4 mt-4">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-md bg-primary/15 text-primary grid place-items-center shrink-0">
                  <MessageCircle className="w-5 h-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-3">
                    <div className="label-overline text-primary">WhatsApp channel</div>
                    <button
                      type="button"
                      onClick={toggleWhatsapp}
                      disabled={savingWhatsapp || !whatsapp?.configured}
                      role="switch"
                      aria-checked={!!whatsapp?.enabled}
                      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${whatsapp?.enabled ? "bg-primary" : "bg-muted"} ${!whatsapp?.configured ? "opacity-40 cursor-not-allowed" : ""}`}
                      data-testid="settings-whatsapp-toggle"
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${whatsapp?.enabled ? "translate-x-6" : "translate-x-1"}`} />
                    </button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                    When on, customer messages try <strong>WhatsApp first</strong> (~75% cheaper than SMS and can carry photos &amp; video), and automatically <strong>fall back to SMS</strong> if the customer isn&apos;t reachable on WhatsApp — nothing is ever lost.
                  </p>
                  {whatsapp?.sandbox && (
                    <p className="text-[11px] text-amber-200/90 mt-2 leading-relaxed" data-testid="settings-whatsapp-sandbox-note">
                      <strong>Sandbox mode:</strong> currently using Twilio&apos;s test number ({whatsapp.from_number}). Recipients must first send the sandbox join code to that number on WhatsApp — perfect for demos. Going live needs a one-off Meta business verification.
                    </p>
                  )}
                  {!whatsapp?.configured && (
                    <p className="text-[11px] text-muted-foreground mt-2">WhatsApp sender not configured on the platform yet.</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : tab === "hours" ? (
          <div className="p-6 space-y-6" data-testid="settings-hours-pane">
            {/* Working hours ---------------------------------------------- */}
            <div className="rounded-lg border border-primary/30 bg-primary/[0.04] p-4 flex items-start gap-3">
              <div className="w-9 h-9 rounded-md bg-primary/15 text-primary grid place-items-center shrink-0">
                <Clock className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <div className="label-overline text-primary">Workshop working hours</div>
                <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                  These drive the <strong className="text-foreground">start-by</strong> maths and the <strong className="text-foreground">ready-for</strong> promise checks on Workshop Control. Any job needing a lift before you open flags as <span className="text-destructive font-semibold">IMPOSSIBLE</span>; any car promised OUT after you close flags as <span className="text-warning font-semibold">OVER-PROMISED</span>. The lunch break adds itself to jobs whose work window crosses it, so the tech doesn&apos;t get held up.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="hours-open" className="label-overline">Open</label>
                <input
                  id="hours-open"
                  type="time"
                  value={hoursOpen}
                  onChange={(e) => setHoursOpen(e.target.value)}
                  className="input-default text-base mt-2 font-mono-plex"
                  data-testid="settings-hours-open"
                />
              </div>
              <div>
                <label htmlFor="hours-close" className="label-overline">Close</label>
                <input
                  id="hours-close"
                  type="time"
                  value={hoursClose}
                  onChange={(e) => setHoursClose(e.target.value)}
                  className="input-default text-base mt-2 font-mono-plex"
                  data-testid="settings-hours-close"
                />
              </div>
            </div>

            <div>
              <div className="label-overline">Open days</div>
              <div className="flex gap-1.5 mt-2">
                {[
                  { i: 0, l: "Mon" }, { i: 1, l: "Tue" }, { i: 2, l: "Wed" },
                  { i: 3, l: "Thu" }, { i: 4, l: "Fri" }, { i: 5, l: "Sat" },
                  { i: 6, l: "Sun" },
                ].map((d) => {
                  const active = hoursDays.includes(d.i);
                  return (
                    <button
                      key={d.i}
                      type="button"
                      onClick={() => setHoursDays(
                        active ? hoursDays.filter((x) => x !== d.i) : [...hoursDays, d.i].sort()
                      )}
                      className={`text-xs font-bold uppercase tracking-wider px-3 py-2 rounded border transition-colors ${
                        active
                          ? "bg-primary text-primary-foreground border-primary"
                          : "bg-transparent text-muted-foreground border-border hover:bg-muted/30"
                      }`}
                      data-testid={`settings-hours-day-${d.i}`}
                    >
                      {d.l}
                    </button>
                  );
                })}
              </div>
              <p className="text-[11px] text-muted-foreground mt-2">
                Days you&apos;re closed are skipped on carry-over — a Friday car that doesn&apos;t collect rolls to Monday, not Saturday.
              </p>
            </div>

            <div className="rounded-lg border border-border p-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="label-overline">Technician lunch break</div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Jobs whose work window crosses lunch get bumped earlier so the promise still holds.
                  </p>
                </div>
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={lunchEnabled}
                    onChange={(e) => setLunchEnabled(e.target.checked)}
                    data-testid="settings-hours-lunch-toggle"
                  />
                  <span className="text-xs font-semibold">{lunchEnabled ? "On" : "Off"}</span>
                </label>
              </div>
              {lunchEnabled && (
                <div className="grid grid-cols-2 gap-4 mt-3">
                  <div>
                    <label htmlFor="hours-lunch-start" className="label-overline">Lunch start</label>
                    <input
                      id="hours-lunch-start"
                      type="time"
                      value={lunchStart}
                      onChange={(e) => setLunchStart(e.target.value)}
                      className="input-default text-base mt-2 font-mono-plex"
                      data-testid="settings-hours-lunch-start"
                    />
                  </div>
                  <div>
                    <label htmlFor="hours-lunch-end" className="label-overline">Lunch end</label>
                    <input
                      id="hours-lunch-end"
                      type="time"
                      value={lunchEnd}
                      onChange={(e) => setLunchEnd(e.target.value)}
                      className="input-default text-base mt-2 font-mono-plex"
                      data-testid="settings-hours-lunch-end"
                    />
                  </div>
                </div>
              )}
            </div>

            {hoursDefaults && (
              <p className="text-[11px] text-muted-foreground font-mono-plex">
                Platform default: {hoursDefaults.open}–{hoursDefaults.close} Mon-Fri · lunch {hoursDefaults.lunch?.start}–{hoursDefaults.lunch?.end}
              </p>
            )}

            <div className="flex justify-end">
              <button
                onClick={saveWorkingHours}
                disabled={savingHours}
                className="btn-primary"
                data-testid="settings-save-hours-btn"
              >
                {savingHours ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Save working hours
              </button>
            </div>
          </div>
        ) : tab === "ai" ? (
          <AiAdvisorPane conf={aiAdvisor} saving={savingAi} onSave={saveAiAdvisor} />
        ) : tab === "videos" ? (
          <ExplainerVideosPane />
        ) : (
          <div />
        )}

        {tab !== "smsbrand" && tab !== "hours" && tab !== "ai" && tab !== "videos" && (
          <div className="flex justify-end gap-3 p-6 border-t border-border">
            <button onClick={onClose} className="btn-outline" data-testid="settings-cancel-btn">Cancel</button>
            <button onClick={save} disabled={saving || loading} className="btn-primary" data-testid="settings-save-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save templates
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
